import { termConfig } from '@1delta/data-sdk'
import { termOfferRateToAprPct } from './math'
import type { TermBookLevel, TermFillNow, TermFillNowSide } from './types'

/**
 * Term Finance Terminal 1 order store — the FILL-NOW limit-order surface.
 *
 * Between sealed-bid auction rounds a Term repo used to be display-only on the
 * borrow side. Terminal 1 adds a maker/taker limit-order book settling into the
 * SAME repo markets: makers post EIP-712 (or on-chain presigned) lend/borrow
 * orders keyed by `repoServicer`, takers fill them on the Terminal 1 diamond
 * (`settleLimitLend` / `settleLimitBorrow`), and the resulting position is an
 * ordinary Term repo position (repo tokens / collateralized debt).
 *
 * Discovery is an open, unauthenticated REST store. Enforcement is on-chain,
 * so a stale store can only under-report — never mis-settle. See
 * TERM_TERMINAL1.md for the full surface.
 *
 * Side semantics (taker/our-user perspective):
 * - a maker LEND order  = fill-now BORROW liquidity (taker borrows at its rate)
 * - a maker BORROW order = fill-now LEND liquidity (taker lends at its rate)
 */
export const DEFAULT_TERM_ORDER_STORE =
  'https://api.global.termfinance.io/protocol'

/**
 * Same globalThis-backed override key data-sdk's `setTermOrderStoreBase`
 * writes. Read DIRECTLY here (rather than importing the new data-sdk
 * resolver) so this module needs NOTHING beyond the already-published
 * data-sdk surface — the data-side go-live is then a margin-fetcher-only
 * publish, the Midnight precedent. The setter stays in data-sdk for the
 * worker (monorepo-built, never npm-bound).
 */
const TERM_ORDER_STORE_OVERRIDE_KEY = '__1delta_term_order_store_base__'

/** Order-store base for a chain: override → config → public hosted store. */
export function termOrderStoreBaseUrl(chainId: string): string {
  const override = (globalThis as any)[TERM_ORDER_STORE_OVERRIDE_KEY]
  if (typeof override === 'string' && override.length > 0) {
    return override.replace(/\/+$/, '')
  }
  // `orderStoreUrl` is a config-JSON field newer than the published data-sdk
  // TYPE — read it loosely so this compiles against the old declaration too.
  const cfg = (termConfig()?.[chainId] as any)?.orderStoreUrl
  if (typeof cfg === 'string' && cfg.length > 0) {
    return cfg.replace(/\/+$/, '')
  }
  return DEFAULT_TERM_ORDER_STORE
}

/** One order as served by `GET {base}/orders?chainId=` (fields we consume). */
export interface TermStoreOrder {
  id: string
  orderKind: 'lend' | 'borrow'
  chainId: number
  /** THE market join key — the repo's TermRepoServicer (NOT termRepoId). */
  repoServicer: string
  /** Order size, purchase-token base units (raw string). */
  purchaseTokenAmount: string
  /**
   * Fixed rate, 1e18-scaled FRACTION annualized on Term's 360-day year — the
   * same convention as auction clearing prices (`termOfferRateToAprPct`).
   */
  offerRate: string
  maker: string
  /** Pinned counterparty; zero address = anyone may fill. */
  taker: string
  /** Unix seconds (stringified uint256; max-uint = good-til-cancelled). */
  expiry: string
  salt: string
  sigType: number
  sigData: string
  isPreSigned: boolean
  orderState: string
  /** Unfilled remainder, purchase-token base units (raw string). */
  remainingAmount: string
  filledAmount: string
  /** Maker's live spendable balance (lend orders; raw string). */
  cachedAvailableBalance?: string
  hasSufficientApproval?: boolean
  /** True for auto-quoted Blue Sheets VAULT liquidity (not a human maker). */
  isSynthetic?: boolean
  /** Fee the order charges the taker (raw string; semantics per order kind). */
  borrowFee?: string
  feeRecipient?: string
  repoToken?: string
}

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
const DEFAULT_TIMEOUT_MS = 8_000

/**
 * Last-known-good store snapshot, per chain. The store sits behind a CDN that
 * can reject DATACENTER egress (a fetch that works from a laptop can 403 from
 * a VPS — observed 2026-08-21: the yield-tracer box read "no liquidity" while
 * the same code elsewhere saw 16.7k USDC of depth), and a silent null here
 * blanks every Term rate card for the whole downstream cache window. Bounded
 * so a genuinely dead store eventually degrades to auction-only.
 */
const STORE_LKG_TTL_MS = 30 * 60_000
const storeLastGood = new Map<
  string,
  { byServicer: Map<string, TermStoreOrder[]>; at: number }
>()

/** Test seam. */
export const __resetTermOrderStoreCache = (): void => {
  storeLastGood.clear()
}

/**
 * A maker order is fillable by an arbitrary taker when it is live, open to
 * anyone, and (for lend orders) actually funded. The store pre-computes the
 * funding checks (`cachedAvailableBalance` / `hasSufficientApproval`); trust
 * them for DISPLAY — actions re-validate on-chain at settle time anyway.
 */
export function fillableRemaining(
  order: TermStoreOrder,
  nowSec: number,
): bigint {
  if (order.taker && order.taker.toLowerCase() !== ZERO_ADDRESS) return 0n
  if (order.hasSufficientApproval === false) return 0n
  let remaining: bigint
  let expiry: bigint
  try {
    remaining = BigInt(order.remainingAmount ?? order.purchaseTokenAmount)
    expiry = BigInt(order.expiry ?? '0')
  } catch {
    return 0n
  }
  if (expiry <= BigInt(nowSec)) return 0n
  if (remaining <= 0n) return 0n
  if (order.cachedAvailableBalance != null) {
    try {
      const avail = BigInt(order.cachedAvailableBalance)
      if (avail < remaining) remaining = avail
    } catch {
      /* ignore malformed balance — keep remaining */
    }
  }
  return remaining
}

/**
 * Fetch the full order store for a chain (ONE request) and group orders by
 * `repoServicer` (lowercased). Returns null on transport failure so callers
 * can distinguish "store down" from "no orders".
 *
 * `filter` (default `'fillable'`) keeps only orders an ARBITRARY taker can
 * fill right now — the book/rate view. `'all'` keeps taker-pinned, unfunded
 * and exhausted rows too: the view a MAKER needs of their own orders.
 */
export async function fetchTermStoreOrders(
  chainId: string,
  fetchImpl: typeof fetch = fetch,
  filter: 'fillable' | 'all' = 'fillable',
): Promise<Map<string, TermStoreOrder[]> | null> {
  const base = termOrderStoreBaseUrl(chainId)
  const url = `${base}/orders?chainId=${chainId}`
  try {
    // Browser-shaped headers on purpose: the store's CDN has rejected
    // header-less datacenter requests while serving identical ones with a UA
    // (the De1 Referer-WAF lesson). One retry covers transient edge refusals.
    let res: Response | undefined
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        // `.call(globalThis, ...)` — Cloudflare Workers reject a bare fetch ref.
        res = await fetchImpl.call(globalThis, url, {
          headers: {
            accept: 'application/json',
            'user-agent':
              'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/126.0 Safari/537.36',
          },
          signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
        })
        if (res.ok) break
      } catch (e) {
        if (attempt === 1) throw e
      }
    }
    if (!res?.ok) {
      // eslint-disable-next-line no-console
      console.warn(
        `[term-order-store] chain ${chainId}: HTTP ${res?.status ?? 'ERR'} from ${base} — serving last-known-good if fresh`,
      )
      return filter === 'fillable' ? staleStoreOrDegrade(chainId) : null
    }
    const json: any = await res.json()
    const orders: TermStoreOrder[] = Array.isArray(json?.orders)
      ? json.orders
      : Array.isArray(json)
        ? json
        : []
    const nowSec = Math.floor(Date.now() / 1000)
    const byServicer = new Map<string, TermStoreOrder[]>()
    for (const order of orders) {
      if (!order?.repoServicer) continue
      if (order.orderKind !== 'lend' && order.orderKind !== 'borrow') continue
      if (filter === 'fillable' && fillableRemaining(order, nowSec) <= 0n)
        continue
      const key = order.repoServicer.toLowerCase()
      const list = byServicer.get(key)
      if (list) list.push(order)
      else byServicer.set(key, [order])
    }
    // Only the FILLABLE view is cached — it is what rates/depth derive from;
    // the 'all' (maker) view must never serve stale rows as live.
    if (filter === 'fillable') {
      storeLastGood.set(chainId, { byServicer, at: Date.now() })
    }
    return byServicer
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(
      `[term-order-store] chain ${chainId}: fetch failed (${(e as any)?.message ?? e}) — serving last-known-good if fresh`,
    )
    return filter === 'fillable' ? staleStoreOrDegrade(chainId) : null
  }
}

/** LKG within TTL, else null (rows degrade to the auction-only picture). */
function staleStoreOrDegrade(
  chainId: string,
): Map<string, TermStoreOrder[]> | null {
  const hit = storeLastGood.get(chainId)
  if (hit && Date.now() - hit.at <= STORE_LKG_TTL_MS) return hit.byServicer
  return null
}

function toLevels(
  orders: TermStoreOrder[],
  loanDecimals: number,
  nowSec: number,
): TermBookLevel[] {
  const scale = 10 ** loanDecimals
  return orders.map((o) => {
    const units = fillableRemaining(o, nowSec)
    return {
      apr: termOfferRateToAprPct(o.offerRate),
      units: units.toString(),
      assets: Number(units) / scale,
    }
  })
}

function reduceSide(levels: TermBookLevel[]): TermFillNowSide | undefined {
  if (levels.length === 0) return undefined
  let units = 0n
  let assets = 0
  for (const l of levels) {
    units += BigInt(l.units)
    assets += l.assets
  }
  return { aprPct: levels[0].apr, units: units.toString(), assets, levels }
}

/**
 * Reduce one repo's store orders to the fill-now summary consumed by the
 * converter: best-executable APR per side + depth + best-first levels.
 */
export function toTermFillNow(
  orders: TermStoreOrder[] | undefined,
  loanDecimals: number,
  nowSec: number = Math.floor(Date.now() / 1000),
): TermFillNow | null {
  if (!orders || orders.length === 0) return null
  // Maker LEND orders → taker BORROW side; cheapest rate first.
  const borrowLevels = toLevels(
    orders
      .filter((o) => o.orderKind === 'lend')
      .sort((a, b) => Number(BigInt(a.offerRate) - BigInt(b.offerRate))),
    loanDecimals,
    nowSec,
  )
  // Maker BORROW orders → taker LEND side; highest rate first.
  const lendLevels = toLevels(
    orders
      .filter((o) => o.orderKind === 'borrow')
      .sort((a, b) => Number(BigInt(b.offerRate) - BigInt(a.offerRate))),
    loanDecimals,
    nowSec,
  )
  const borrow = reduceSide(borrowLevels)
  const lend = reduceSide(lendLevels)
  if (!borrow && !lend) return null
  return { borrow, lend }
}
